import { InputCpf, InputDefault, InputPhone } from '@/@presentation/components/form';
import { Row, Form as AntForm, DatePicker, Select, FormInstance, Checkbox, Typography } from 'antd';
import React from 'react';
import { styles } from '../../styles';
import { ProcedureContextData } from '@/@core/modules/procedure/domain/procedure.entities';
import { validateCPF } from '@/@core/modules/common/infra/validateCPF';
import { ConfirmationView } from '@/@presentation/modules/procedure/components/confirmationView';
import moment from 'moment';
import { InputRg } from '@/@presentation/components/form/InputRg';
import { useParams } from 'react-router-dom';
import { useDebouncedCallback } from '@/@presentation/hooks/useDebouncedCallback';

interface IProps {
  handleSearchPatientByCpf: (cpf: string) => void;
  handleSearchPatientByRne: (rne: string) => void;
  handleClearPatientCache: () => void;
  states: ProcedureContextData['states'];
  isConfirm: boolean;
  form: FormInstance<any>;
  changehPatient: () => void;
  setHasError: (hasError: boolean) => void;
  calcAge: () => void;
}

export const Patient: React.FC<IProps> = (props) => {
  const [invalidCpf, setInvalidCpf] = React.useState(false)
  const [withoutCpf, setWithoutCpf] = React.useState(false)
  const { id } = useParams();

  const { calcAge } = props

  const cpfError = props.form.getFieldError('patientCpf');
  const hasCpfError = cpfError.length > 0

  const formItems = props.form.getFieldsValue()

  const handleChange = (cpf: string): void => {
    cpf = cpf.replace(/\D/g, '')
    if (cpf.length === 11) {
      const isInvalid = !validateCPF(cpf)
      if (!isInvalid) {
        props.handleSearchPatientByCpf(cpf)
      } else {
        props.handleClearPatientCache()
      }
      props.changehPatient()
      setInvalidCpf(isInvalid)
      props.setHasError(isInvalid)
      calcAge()
    } else if (cpf.length === 0) {
      // Limpar cache quando CPF for removido completamente
      props.handleClearPatientCache()
      props.changehPatient()
      setInvalidCpf(false)
      props.setHasError(false)
    }
  }

  const handleChangeRne = useDebouncedCallback((value) => {
    if (formItems.nationality !== 'BR' && withoutCpf) {
      if (value && value.trim().length > 0) {
        props.handleSearchPatientByRne(value)
      } else {
        // Limpar cache quando RNE for removido
        props.handleClearPatientCache()
      }
      props.changehPatient()
      calcAge()
    }
  }, 1000)

  const toggleWithoutCpf = () => {
    setWithoutCpf(prev => {
      const next = !prev;

      // Limpar cache do paciente ao alternar entre CPF e RNE
      props.handleClearPatientCache();

      if (!next) {
        // Quando desmarcar paciente estrangeiro (voltar para brasileiro)
        const hasExistingNationality = props.states?.data?.patient?.nationality && 
                                      props.states?.data?.patient?.nationality !== 'BR';
        
        if (!id || !hasExistingNationality) {
          props.form.setFieldsValue({
            nationality: 'BR',
            patientInternationalDocument: undefined,
          });
        }
      } else {
        // Quando marcar paciente estrangeiro - CPF some e fica zerado
        const hasExistingData = props.states?.data?.patient?.nationality || 
                               props.states?.data?.patient_contact?.international_document;
        
        if (!id || !hasExistingData) {
          props.form.setFieldsValue({
            nationality: '',
          });
        }
        
        // Sempre zerar o CPF quando marcar como estrangeiro
        props.form.setFieldsValue({
          patientCpf: undefined,
        });
      }

      props.form.validateFields([
        'nationality',
        'patientInternationalDocument',
      ]);

      return next;
    });
  };

  React.useEffect(() => {
    props.states?.data?.patient?.birthday_date && calcAge()
    
    // Resetar o estado primeiro
    setWithoutCpf(false)
    
    // Só marcar checkbox quando EDITANDO paciente (não na criação)
    // Verifica se há dados reais do paciente carregados
    const hasPatientData = props.states?.data?.patient?.cpf || props.states?.data?.patient?.name;
    
    if (hasPatientData) {
      // Marcar checkbox quando editar paciente com:
      // nacionalidade diferente de BR E sem CPF preenchido
      const nationality = props.states?.data?.patient?.nationality;
      const hasCpf = props.states?.data?.patient?.cpf;
      
      const isNonBrazilian = nationality && nationality !== 'BR';
      const noCpf = !hasCpf;
      
      if (isNonBrazilian && noCpf) {
        setWithoutCpf(true)
      }
    }
    // Durante a criação (sem dados do paciente), deixar withoutCpf como false (padrão)
  }, [props.states?.data?.patient, props.states?.data?.patient_contact])

  return (
    <div>
      <Row>
        {!withoutCpf ? (
          <div style={{ marginRight: 20 }}>
            {props.isConfirm ? (
              <ConfirmationView item={{
                label: 'CPF',
                value: formItems.patientCpf || ''
              }} />
            ) :
              <InputCpf
                name="patientCpf"
                label="CPF"
                placeholder='Digite o CPF'
                disabled={id ? true : false}
                required={!withoutCpf}
                onChange={(e) => handleChange(e.target.value)}
                error={{ isError: (invalidCpf || hasCpfError) && !withoutCpf, message: invalidCpf ? 'CPF invalido!' : cpfError[0] }}
              />}
          </div>
        ) : (
          <div style={{ marginRight: 20 }}>
            {props.isConfirm ? (
              <ConfirmationView item={{
                label: 'RNE/Passaporte',
                value: formItems.patientInternationalDocument || '',
              }} />
            ) : (
              <InputDefault
                label="RNE/Passaporte"
                name="patientInternationalDocument"
                placeholder="RNE/Passaporte"
                nativeProps={{
                  disabled: props.isConfirm,
                  onChange: (e) => {
                    if (formItems.nationality !== 'BR' && withoutCpf) {
                      handleChangeRne(e.target.value)
                    }
                  }
                }}
              />
            )}
          </div>
        )}
        {
          props.isConfirm ? (
            <ConfirmationView item={{
              label: 'Nome completo',
              value: formItems.patientFullName || ''
            }} />
          ) : (
            <InputDefault
              label="Nome completo"
              name="patientFullName"
              required={true}
              placeholder="Digite o nome completo do paciente"
              nativeProps={{
                style: {
                  width: 400,
                },
                disabled: props.isConfirm
              }}
            />
          )
        }
      </Row>
      <Row>
        <Checkbox style={{ marginTop: -10, marginBottom: 10 }} checked={withoutCpf} onClick={toggleWithoutCpf}>
          <Typography style={{ marginTop: 4, fontSize: 12 }}>
            Paciente estrangeiro sem CPF
          </Typography>
        </Checkbox>
      </Row>
      <Row>
        <div style={{ marginRight: 20 }}>
          {props.isConfirm ? (
            <ConfirmationView item={{
              label: 'RG',
              value: formItems.patientRg || ''
            }} />
          ) : (
            <InputRg label="RG" name="patientRg" placeholder="Digite o RG do paciente" />
          )}
        </div>
        <div style={{ marginRight: 20 }}>
          {
            props.isConfirm ? (
              <ConfirmationView item={{
                label: 'Data de nascimento',
                value: moment(formItems.patientBirthDate?.toDate()).format('DD/MM/YYYY') || ''
              }} />
            ) : (
              <AntForm.Item
                name={'patientBirthDate'}
                label="Data de nascimento"
              >
                <DatePicker
                  disabled={props.isConfirm}
                  style={{ ...styles.defaultInput, borderRadius: 8 }}
                  format="DD/MM/YYYY"
                  onChange={calcAge}
                  onClick={calcAge}
                  onMouseLeave={calcAge}
                  onSelect={calcAge}
                  onKeyDown={calcAge}
                  allowClear={false}
                />
              </AntForm.Item>
            )
          }
        </div>
        <div style={{ marginRight: 20 }}>
          {
            props.isConfirm ? (
              <ConfirmationView item={{
                label: 'Idade',
                value: formItems.patientAge || ''
              }} />
            ) : (
              <InputDefault
                label="Idade"
                name="patientAge"
                placeholder=""
                nativeProps={{
                  style: {
                    width: 70,
                  },
                  disabled: true
                }}
              />
            )
          }
        </div>
        {
          props.isConfirm ? (
            <ConfirmationView item={{
              label: 'Sexo',
              value: formItems.patientGenre ? props.states.genres.filter(item => item.value === formItems.patientGenre)[0]?.label : ''
            }} />
          ) : (
            <AntForm.Item name="patientGenre" label="Sexo">
              <Select
                disabled={props.isConfirm}
                data-testid="select-procedure-patient-genre"
                style={{ ...styles.defaultInput, width: 140 }}
                placeholder="Selecione"
                showSearch
                filterOption={(input, option: any) => {
                  return (
                    option.value?.toLowerCase().indexOf(input?.toLowerCase()) >= 0 ||
                    option.label?.toLowerCase().indexOf(input?.toLowerCase()) >= 0 ||
                    option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
                  );
                }}
              >
                {props.states.genres.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </AntForm.Item>
          )
        }
      </Row>
      <Row>
        <div style={{
          marginRight: 20,
        }}>
          {
            props.isConfirm ? (
              <ConfirmationView item={{
                label: 'E-mail',
                value: formItems.patientEmail || ''
              }} />
            ) : (
              <InputDefault
                label="E-mail"
                name="patientEmail"
                placeholder="Digite o e-mail do paciente"
                nativeProps={{
                  type: "email",
                  style: {
                    width: 300,
                  },
                  disabled: props.isConfirm
                }}
              />
            )
          }
        </div>
        <div
          style={{
            marginRight: 20,
          }}
        >
          {
            props.isConfirm ? (
              <ConfirmationView item={{
                label: 'Telefone',
                value: formItems.patientPhone || ''
              }} />
            ) : (
              <InputPhone name="patientPhone" label="Telefone" />
            )
          }
        </div>
        {
          props.isConfirm ? (
            <ConfirmationView item={{
              label: 'Nacionalidade',
              value: formItems.nationality ? 
                (props.states.nationality.find(n => n.sigla === formItems.nationality)?.nome_pais || formItems.nationality) : '',
            }} />
          ) : (
            <AntForm.Item
              name="nationality"
              label="Nacionalidade"
              rules={[
                {
                  required: withoutCpf,
                  message: 'Nacionalidade é obrigatória para pacientes estrangeiros sem CPF',
                },
              ]}
            >
              <Select
                disabled={props.isConfirm}
                data-testid="select-doctorName"
                style={{ ...styles.defaultInput, minWidth: 260 }}
                placeholder="Selecione"
                showSearch
                filterOption={(input, option: any) => {
                  return (
                    option.value?.toLowerCase().indexOf(input?.toLowerCase()) >= 0 ||
                    option.label?.toLowerCase().indexOf(input?.toLowerCase()) >= 0 ||
                    option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
                  );
                }}
              >
                {
                  props.states.nationality
                    .filter(i => i.sigla !== (withoutCpf ? 'BR' : ''))
                    .map((option) => (
                      <Select.Option key={option.sigla} value={option.sigla}>
                        {option.nome_pais}
                      </Select.Option>
                    ))}
              </Select>
            </AntForm.Item>
          )
        }
      </Row>
      {!withoutCpf && (
        <Row>
          <div style={{ marginRight: 20 }}>
            {props.isConfirm ? (
              <ConfirmationView item={{
                label: 'RNE/Passaporte',
                value: formItems.patientInternationalDocument || '',
              }} />
            ) : (
              <InputDefault
                label="RNE/Passaporte"
                name="patientInternationalDocument"
                placeholder="RNE/Passaporte"
                nativeProps={{
                  disabled: props.isConfirm,
                  onChange: (e) => {
                    if (formItems.nationality !== 'BR' && withoutCpf) {
                      handleChangeRne(e.target.value)
                    }
                  }
                }}
              />
            )}
          </div>
        </Row>
      )}
    </div>
  )
};
